import React from 'react';
import { Sparkles, Facebook, Instagram, Mail, Phone, MapPin, Heart } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Footer = () => {
  const { t } = useTranslation();
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-purple-900 to-pink-900 text-white">
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <img 
                src="/logo-dm.png" 
                alt="Décors Multifestif" 
                className="w-16 h-16 object-contain"
              />
              <div>
                <div className="text-xl font-bold bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                  Décors Multifestif
                </div>
                <div className="text-sm text-gray-400">Montréal</div>
              </div>
            </div>
            <p className="text-gray-300 leading-relaxed">
              {t("footer.about.description")}
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/martin2527"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 hover:bg-gradient-to-r hover:from-orange-500 hover:to-pink-600 rounded-full flex items-center justify-center transition-all hover:scale-110"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/martindoudou"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 hover:bg-gradient-to-r hover:from-pink-500 hover:to-fuchsia-600 rounded-full flex items-center justify-center transition-all hover:scale-110"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-orange-400">{t("footer.navigation.title")}</h3>
            <ul className="space-y-3">
              {[
                { label: t('nav.home'), id: 'hero' },
                { label: t('nav.services'), id: 'services' },
                { label: t('nav.gallery'), id: 'gallery' },
                { label: t('nav.location'), id: 'rentals' },
                { label: t('nav.pricing'), id: 'pricing' },
                { label: t('nav.testimonials'), id: 'testimonials' },
                { label: t('nav.contact'), id: 'contact' }
              ].map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => scrollToSection(item.id)}
                    className="text-gray-300 hover:text-orange-400 transition-colors"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-pink-400">{t("footer.services.title")}</h3>
            <ul className="space-y-3 text-gray-300">
              <li>{t("footer.services.weddings")}</li>
              <li>{t("footer.services.birthdays")}</li>
              <li>{t("footer.services.corporate")}</li>
              <li>{t("footer.services.babyshower")}</li>
              <li>{t("footer.services.inaugurations")}</li>
              <li>{t("footer.services.themed")}</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-fuchsia-400">{t("footer.contact.title")}</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-orange-400 flex-shrink-0 mt-1" />
                <span className="text-gray-300">
                  Montréal, Québec<br />
                  Canada
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-pink-400 flex-shrink-0" />
                <a href="tel:+15143215555" className="text-gray-300 hover:text-orange-400 transition-colors">
                  (514) 321-5555
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-fuchsia-400 flex-shrink-0" />
                <a href="mailto:martindoucet184@gmail.com" className="text-gray-300 hover:text-pink-400 transition-colors">
                  martindoucet184@gmail.com
                </a>
              </li>
            </ul>
            <div className="mt-6">
              <p className="text-sm text-gray-400">
                Lun-Sam: 9h-19h<br />
                Dimanche: Sur rendez-vous
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm text-center md:text-left">
              © {currentYear} Décors Multifestif. Tous droits réservés.
            </p>
            <p className="text-gray-400 text-sm flex items-center gap-2">
              {t("footer.madeWith")} <Heart className="w-4 h-4 text-pink-500 fill-pink-500 animate-pulse" /> pour vos événements spectaculaires
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
